import type { ComponentType } from "react";
import { BLOCK_TYPES, type Block, type PresetBlock } from "../contract/types";
import type { BlockViewProps } from "./types";

import { Prose } from "./Prose";
import { Callout } from "./Callout";
import { Formula } from "./Formula";
import { MetricCards } from "./MetricCards";
import { DataTable } from "./DataTable";
import { FinancialStatementBlock } from "./FinancialStatementBlock";
import { Chart } from "./Chart";
import { Timeline } from "./Timeline";
import { Compare } from "./Compare";
import { Quiz } from "./Quiz";
import { WorkedExample } from "./WorkedExample";
import { Simulator } from "./Simulator";
import { Flashcards } from "./Flashcards";
import { Rubric } from "./Rubric";
import { SourceList } from "./SourceList";
import { Preset } from "./Preset";

/**
 * Registro de bloques: `type` → componente.
 *
 * El mapa está tipado con un tipo mapeado sobre `BLOCK_TYPES`, así que si el
 * contrato gana un átomo y nadie lo registra, **no compila**. Esa es la razón
 * de escribirlo así y no como un `Record<string, …>` suelto.
 *
 * `preset` vive fuera de `BLOCK_TYPES` (esa constante lista los átomos), pero
 * es un miembro más de la unión `Block`: el backend lo emite como un
 * `block.add` normal, así que tiene que estar registrado o al recargar la
 * conversación los presets caerían en `Unknown`.
 */

export type AtomType = (typeof BLOCK_TYPES)[number];
export type BlockType = AtomType | "preset";
export type BlockOf<T extends BlockType> = Extract<Block, { type: T }>;

export type BlockRegistry = {
  [K in AtomType]: ComponentType<BlockViewProps<BlockOf<K>>>;
} & {
  preset: ComponentType<BlockViewProps<PresetBlock>>;
};

export const blockRegistry: BlockRegistry = {
  prose: Prose,
  callout: Callout,
  formula: Formula,
  metric_cards: MetricCards,
  data_table: DataTable,
  financial_statement: FinancialStatementBlock,
  chart: Chart,
  timeline: Timeline,
  compare: Compare,
  quiz: Quiz,
  worked_example: WorkedExample,
  simulator: Simulator,
  flashcards: Flashcards,
  rubric: Rubric,
  source_list: SourceList,
  preset: Preset,
};

export function isKnownBlockType(type: string): type is BlockType {
  return type === "preset" || (BLOCK_TYPES as readonly string[]).includes(type);
}

/**
 * Devuelve el componente de un tipo, o `null` si el contrato trae algo que
 * esta versión de la UI no conoce. El único punto donde se relaja el tipado
 * del registro: el `block` ya viene validado como unión discriminada, pero el
 * índice por una clave arbitraria pierde esa relación.
 */
export function getBlockComponent(type: string): ComponentType<BlockViewProps<Block>> | null {
  if (!isKnownBlockType(type)) return null;
  const component = blockRegistry[type] as unknown as ComponentType<BlockViewProps<Block>>;
  return component ?? null;
}

